import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function Users() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  
  // Permission checks
  const canUpdateStatus = hasPermission('users.status');
  const canEdit = hasPermission('users.update');
  const canDelete = hasPermission('users.delete');

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    email_verified: '',
  });

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, filters]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });

      const response = await usersAPI.getUsers(params);
      setUsers(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Error loading users. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleStatusChange = async (userId, newStatus) => {
    if (!canUpdateStatus) {
      alert('You do not have permission to change user status');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to ${newStatus} this user?`)) return;

    try {
      await usersAPI.updateUserStatus(userId, newStatus);
      alert('User status updated successfully');
      fetchUsers();
    } catch (error) {
      alert('Error updating user status');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Users Management</h1>
        <p>Manage user accounts and their statuses</p>
      </div>

      <div className="card">
        <div className="filters">
          <div className="filter-item">
            <input
              type="text"
              className="form-control"
              placeholder="Search by email or name"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
          <div className="filter-item">
            <select
              className="form-control"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="filter-item">
            <select
              className="form-control"
              value={filters.email_verified}
              onChange={(e) => handleFilterChange('email_verified', e.target.value)}
            >
              <option value="">All Verification</option>
              <option value="true">Verified</option>
              <option value="false">Not Verified</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading users...</div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Serial Number</th>
                    <th>Status</th>
                    <th>Verified</th>
                    <th>Devices</th>
                    <th>Sessions</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>{user.email}</td>
                      <td>{user.name}</td>
                      <td>{user.serial_number || '-'}</td>
                      <td>
                        <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${user.email_verified ? 'badge-success' : 'badge-warning'}`}>
                          {user.email_verified ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>{user.device_count}</td>
                      <td>{user.session_count}</td>
                      <td>{user.last_login_at ? new Date(user.last_login_at).toLocaleDateString() : '-'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                          {/* View - always visible */}
                          <button
                            className="btn btn-primary"
                            style={{ padding: '5px 10px', fontSize: '12px' }}
                            onClick={() => navigate(`/users/${user.id}`)}
                          >
                            View
                          </button>
                          
                          {/* Status change - only if has permission */}
                          {canUpdateStatus && (
                            user.is_active ? (
                              <button
                                className="btn btn-danger"
                                style={{ padding: '5px 10px', fontSize: '12px' }}
                                onClick={() => handleStatusChange(user.id, 'inactive')}
                              >
                                Suspend
                              </button>
                            ) : (
                              <button
                                className="btn btn-success"
                                style={{ padding: '5px 10px', fontSize: '12px' }}
                                onClick={() => handleStatusChange(user.id, 'active')}
                              >
                                Activate
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              <span>
                Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Users;